import { Link } from 'react-router-dom';
import { FileText, Scale, AlertCircle, CheckCircle, Shield, Users } from 'lucide-react';

function TermsOfService() {
  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="bg-black py-24 relative overflow-hidden">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="flex items-center justify-center mb-6">
            <Scale className="w-20 h-20 text-gold" />
          </div>
          <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">Legal Terms</span>
          <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Terms of <span className="text-gold">Service</span>
          </h1>
          <p className="text-gray-300 text-xl max-w-4xl mx-auto">
            Please read these terms and conditions carefully before using our services or website.
          </p>
          <p className="text-gray-400 text-sm mt-4">
            Last Updated: January 2025
          </p>
        </div>
      </section>

      {/* Overview Cards */}
      <section className="bg-black py-16 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-black border border-gold/30 rounded-lg p-8 hover:border-gold transition-all">
              <CheckCircle className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-white font-bold text-xl mb-3">Service Agreement</h3>
              <p className="text-gray-400">Terms governing our construction and facility services</p>
            </div>
            <div className="bg-black border border-gold/30 rounded-lg p-8 hover:border-gold transition-all">
              <AlertCircle className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-white font-bold text-xl mb-3">Your Obligations</h3>
              <p className="text-gray-400">Responsibilities and requirements for using our services</p>
            </div>
            <div className="bg-black border border-gold/30 rounded-lg p-8 hover:border-gold transition-all">
              <Shield className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-white font-bold text-xl mb-3">Legal Protection</h3>
              <p className="text-gray-400">Limitations of liability and dispute resolution</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="bg-black py-16 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-gray-300 space-y-12">
            
            {/* Section 1: Acceptance of Terms */}
            <div className="pt-28">
              <div className="flex items-center space-x-3 mb-6">
                <FileText className="w-8 h-8 text-gold" />
                <h2 className="text-3xl font-bold text-white font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  1. Acceptance of Terms
                </h2>
              </div>
              <div className="space-y-4 text-lg">
                <p>
                  By accessing or using the Williams Diversified LLC website (the "Site") or engaging our services, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Site or services.
                </p>
                <p>
                  These Terms constitute a legally binding agreement between you and Williams Diversified LLC ("we," "us," or "our"). We reserve the right to modify these Terms at any time. Your continued use of the Site or services after changes are posted constitutes acceptance of the modified Terms.
                </p>
              </div>
            </div>

            {/* Section 2: Services */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                2. Services Provided
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  Williams Diversified LLC provides construction, project management, facility services, and related contracting services (collectively, "Services") to commercial, private, and government clients. Services are provided subject to:
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Execution of a separate written service agreement or contract</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Acceptance of project scope, specifications, and terms</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Compliance with applicable federal, state, and local regulations</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Availability of resources, materials, and qualified personnel</span>
                  </li>
                </ul>
                <p className="mt-4">
                  The specific terms of any service engagement will be governed by the individual contract or agreement executed for that project, which will take precedence over these general Terms in case of conflict.
                </p>
              </div>
            </div>

            {/* Section 3: Website Use */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                3. Website Use and Restrictions
              </h2>
              <div className="space-y-4 text-lg">
                <p>You may use our Site for lawful purposes only. You agree not to:</p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Use the Site in any way that violates applicable federal, state, or local laws</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Transmit any harmful code, viruses, or malicious software</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Attempt to gain unauthorized access to our systems or networks</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Interfere with or disrupt the Site's functionality</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Collect or harvest information from the Site using automated means</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Reproduce, duplicate, copy, or resell any part of the Site without permission</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 4: Intellectual Property */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                4. Intellectual Property Rights
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  All content on this Site, including but not limited to text, graphics, logos, images, software, and design elements, is the property of Williams Diversified LLC or its licensors and is protected by United States and international copyright, trademark, and other intellectual property laws.
                </p>
                <p>
                  You may not use, reproduce, modify, or distribute any content from this Site without our prior written permission. The Williams Diversified LLC name and logo are trademarks of Williams Diversified LLC.
                </p>
              </div>
            </div>

            {/* Section 5: Quotes and Estimates */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                5. Quotes, Estimates, and Project Proposals
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  Any quotes, estimates, or proposals provided through our Site or communications are:
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Preliminary in nature</strong> and subject to site inspection, detailed scope review, and material/labor cost verification</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Valid for 30 days</strong> unless otherwise specified</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Not binding</strong> until a formal contract or service agreement is executed by both parties</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Subject to change</strong> based on actual conditions, material costs, permits, and unforeseen circumstances</span>
                  </li>
                </ul>
                <p className="mt-4">
                  No work will commence until a written agreement is signed and any required deposits or advance payments are received.
                </p>
              </div>
            </div>

            {/* Section 6: Payment Terms */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                6. Payment Terms
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  Payment terms for services will be specified in individual service agreements. General payment conditions include:
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Payment schedules based on project milestones or progress billing</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Deposits or advance payments may be required before work commences</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Late payments may be subject to interest charges and suspension of work</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Final payment due upon project completion and acceptance</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>We reserve the right to place liens for non-payment as permitted by law</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 7: Warranties and Disclaimers */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                7. Warranties and Disclaimers
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  <strong className="text-white">Service Warranties:</strong> Warranties for our services will be specified in individual service agreements. We stand behind the quality of our work and will address defects in workmanship within the warranty period specified in your contract.
                </p>
                <p>
                  <strong className="text-white">Website Disclaimer:</strong> This Site and all information provided are on an "as is" and "as available" basis. We make no warranties, express or implied, regarding:
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>The accuracy, completeness, or reliability of information on the Site</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Uninterrupted or error-free operation of the Site</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>The results that may be obtained from using the Site</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 8: Limitation of Liability */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                8. Limitation of Liability
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  To the fullest extent permitted by law, Williams Diversified LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, revenue, data, or use, arising from:
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Your use of or inability to use the Site</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Any errors or omissions in Site content</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Any unauthorized access to our servers or databases</span>
                  </li>
                </ul>
                <p className="mt-4">
                  Liability for services rendered will be addressed in individual service agreements. Nothing in these Terms shall limit liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be excluded by law.
                </p>
              </div>
            </div>

            {/* Section 9: Indemnification */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                9. Indemnification
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  You agree to indemnify, defend, and hold harmless Williams Diversified LLC, its officers, directors, employees, agents, and contractors from any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from:
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Your use of the Site or services</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Your violation of these Terms</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Your violation of any rights of another party</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Any breach of representations or warranties you have made</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 10: Dispute Resolution */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                10. Dispute Resolution and Governing Law
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  <strong className="text-white">Governing Law:</strong> These Terms shall be governed by and construed in accordance with the laws of the State of Oklahoma, without regard to its conflict of law provisions.
                </p>
                <p>
                  <strong className="text-white">Dispute Resolution:</strong> Any disputes arising from these Terms or your use of our Site shall first be addressed through good faith negotiation. If unresolved within 30 days, disputes may be submitted to mediation or arbitration as mutually agreed.
                </p>
                <p>
                  <strong className="text-white">Venue:</strong> Any legal action or proceeding shall be brought exclusively in the state or federal courts located in Tulsa County, Oklahoma, and you consent to the jurisdiction of such courts.
                </p>
                <p className="mt-4">
                  Individual service agreements may contain specific dispute resolution procedures, which will govern disputes related to those services.
                </p>
              </div>
            </div>

            {/* Section 11: Termination */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                11. Termination
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  We reserve the right to terminate or suspend your access to the Site immediately, without prior notice or liability, for any reason, including breach of these Terms.
                </p>
                <p>
                  Termination of service contracts will be governed by the terms specified in individual agreements. All provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
                </p>
              </div>
            </div>

            {/* Section 12: Severability */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                12. Severability and Entire Agreement
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that these Terms remain in full force and effect.
                </p>
                <p>
                  These Terms, together with our Privacy Policy and any service agreements you enter into with us, constitute the entire agreement between you and Williams Diversified LLC regarding use of the Site and supersede any prior agreements.
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-black border border-gold/30 rounded-lg p-8 mt-12">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                13. Contact Information
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  If you have questions about these Terms of Service, please contact us:
                </p>
                <div className="space-y-3 mt-6">
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[120px]">Company:</div>
                    <div className="pt-28">Williams Diversified LLC</div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[120px]">Email:</div>
                    <div className="pt-28">
                      <a href="mailto:inquire@williamsdiversified.com" className="hover:text-gold transition-colors">
                        inquire@williamsdiversified.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[120px]">Phone:</div>
                    <div className="pt-28">
                      <a href="tel:9184933418" className="hover:text-gold transition-colors">
                        (918) 493-3418
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[120px]">Location:</div>
                    <div className="pt-28">Tulsa, Oklahoma</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Ready to <span className="text-gold">Get Started?</span>
          </h3>
          <p className="text-gray-300 text-xl mb-10">
            Contact us to discuss your project and receive a detailed proposal
          </p>
          <Link 
            to="/contact"
            className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}

export default TermsOfService;
